/**
 * 83. Fleet Vehicles — vehicle master; trips; fuel; maintenance; cost.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatQuantity, formatMoney } from "@/lib/money";

interface VehicleRow {
  id: string;
  vehicleNumber: string;
  registration: string;
  makeModel: string;
  driver: string | null;
  odometer: string;
  fuelMonthCost: string;
  currency: string;
  status: "Active" | "InService" | "OutOfService" | "Retired";
}

export function FleetVehiclesPage() {
  const { data, isLoading } = useApiList<VehicleRow>(
    ["fleet", "vehicles"],
    "/fleet/vehicles",
  );

  return (
    <div>
      <PageHeader
        title="Fleet Vehicles"
        subtitle="Vehicle master, trips, fuel, maintenance, and cost."
        actions={<Button variant="primary">Add vehicle</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Vehicles (${data.total})`}>
          <DataTable<VehicleRow>
            rows={data.items}
            columns={[
              {
                key: "vehicleNumber",
                label: "Vehicle",
                render: (r) => (
                  <span className="atlas-mono">{r.vehicleNumber}</span>
                ),
              },
              {
                key: "registration",
                label: "Registration",
                render: (r) => (
                  <span className="atlas-mono">{r.registration}</span>
                ),
              },
              { key: "makeModel", label: "Make / model" },
              {
                key: "driver",
                label: "Driver",
                render: (r) => r.driver ?? "—",
              },
              {
                key: "odometer",
                label: "Odometer",
                numeric: true,
                render: (r) => formatQuantity(r.odometer, 0),
              },
              {
                key: "fuelMonthCost",
                label: "Fuel (month)",
                numeric: true,
                render: (r) => formatMoney(r.fuelMonthCost, r.currency),
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Active" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Retired" ? (
                    <Badge tone="default">{r.status}</Badge>
                  ) : (
                    <Badge
                      tone={r.status === "OutOfService" ? "danger" : "warning"}
                    >
                      {r.status}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
